/**
 * 邮件链接（找回密码 / 邮件登录）的客户端 API。分层与 `emailAuthApi.ts` 一致：
 * 本地预检 → 传输 → 把服务端错误翻成人话。
 */
import type { PublicAccount } from '@poker/shared'

import { getAuthSession } from '../lobby/useAuth.js'
import { AuthError, parseJsonBody, readApiError, type AuthSession } from './session.js'
import { passwordProblem, emailProblem } from './emailAuthApi.js'
import type { EmailLinkPurpose } from '../app/emailLinkToken.js'

export type EmailLinkFailureKind = 'network' | 'rate-limited' | 'token-invalid' | 'invalid' | 'server'

export class EmailLinkError extends Error {
  constructor(
    override readonly message: string,
    readonly kind: EmailLinkFailureKind,
    readonly retryAfterMs: number | null = null,
  ) {
    super(message)
    this.name = 'EmailLinkError'
  }
}

export interface EmailLinkApiOptions {
  readonly auth?: AuthSession
  readonly fetchImpl?: typeof fetch
}

function waitHint(retryAfterMs: number | null): string {
  if (retryAfterMs === null || retryAfterMs <= 0) return '过一会儿再试'
  const mins = Math.ceil(retryAfterMs / 60_000)
  return mins <= 1 ? '一分钟后再试' : `${String(mins)} 分钟后再试`
}

function describe(kind: EmailLinkFailureKind, retryAfterMs: number | null): string {
  switch (kind) {
    case 'network':
      return '网络不通，检查一下再试'
    case 'rate-limited':
      return `试得太频繁，${waitHint(retryAfterMs)}`
    // ★ 必须带「怎么办」：只说「链接无效」用户会以为账号出了问题，反复点同一个链接
    case 'token-invalid':
      return '这个链接已经用过或者过期了，回登录页重新发一封'
    case 'invalid':
      return '密码至少 8 位'
    default:
      return '没能完成，再试一次'
  }
}

function classify(err: AuthError): EmailLinkFailureKind {
  if (err.code === 'E_NETWORK' || err.status === 0) return 'network'
  if (err.status === 429 || err.code === 'E_RATE_LIMITED') return 'rate-limited'
  if (err.status === 401) return 'token-invalid'
  if (err.status === 400) return 'invalid'
  return 'server'
}

function toLinkError(e: unknown): EmailLinkError {
  if (e instanceof EmailLinkError) return e
  const err = e instanceof AuthError ? e : new AuthError(String(e), 'E_INTERNAL', 0, false)
  const kind = classify(err)
  return new EmailLinkError(describe(kind, err.retryAfterMs), kind, err.retryAfterMs)
}

function resolve(options: EmailLinkApiOptions): { auth: AuthSession; fetchImpl: typeof fetch } {
  return {
    auth: options.auth ?? getAuthSession(),
    fetchImpl: options.fetchImpl ?? ((...args) => globalThis.fetch(...args)),
  }
}

async function post(input: {
  auth: AuthSession
  fetchImpl: typeof fetch
  path: string
  body: unknown
}): Promise<unknown> {
  let res: Response
  try {
    res = await input.fetchImpl(`${input.auth.baseUrl()}/api${input.path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input.body),
    })
  } catch (e) {
    throw new AuthError(`网络不可用：${String(e)}`, 'E_NETWORK', 0, false)
  }
  const parsed = parseJsonBody(await res.text())
  if (!res.ok) throw readApiError(res.status, parsed)
  return parsed
}

/**
 * 请求一封链接信。
 *
 * ★ 服务端对「这个邮箱没注册过」也回 200，**客户端不该制造出这个差别**：
 *   这里永远 resolve（除了格式错与限流），界面上也必须写成「如果这个邮箱注册过…」。
 */
export async function requestEmailLink(
  email: string,
  purpose: EmailLinkPurpose,
  options: EmailLinkApiOptions = {},
): Promise<void> {
  const { auth, fetchImpl } = resolve(options)
  const problem = emailProblem(email)
  if (problem !== null) throw new EmailLinkError(problem, 'invalid')
  try {
    await post({
      auth,
      fetchImpl,
      path: '/auth/email-link/request',
      body: { email: email.trim(), deviceId: auth.deviceId(), purpose },
    })
  } catch (e) {
    throw toLinkError(e)
  }
}

interface PairResponse {
  readonly account: PublicAccount
  readonly accessToken: string
  readonly refreshToken: string
  readonly expiresIn: number
  readonly expiresAt?: number
}

/**
 * 兑换链接。成功后 `auth.adopt(pair)` —— 与 `loginWithEmail` 完全一致。
 *
 * 之所以敢把 adopt 放在 api 层：调用方在提交**之前**已经过了确认闸
 * （当前账号还坐在牌桌上时先问一次），闸在前面，就没有「用户还没同意就换了身份」的窗口。
 */
export async function redeemEmailLink(
  token: string,
  password: string | null,
  options: EmailLinkApiOptions = {},
): Promise<PublicAccount> {
  const { auth, fetchImpl } = resolve(options)
  if (password !== null) {
    const problem = passwordProblem(password)
    if (problem !== null) throw new EmailLinkError(problem, 'invalid')
  }
  try {
    const body = (await post({
      auth,
      fetchImpl,
      path: '/auth/email-link/redeem',
      body: {
        token,
        deviceId: auth.deviceId(),
        ...(password === null ? {} : { password }),
      },
    })) as Partial<PairResponse> | null
    if (
      body == null ||
      typeof body.accessToken !== 'string' ||
      typeof body.refreshToken !== 'string' ||
      body.account == null
    ) {
      throw new EmailLinkError(describe('server', null), 'server')
    }
    return auth.adopt(body as PairResponse)
  } catch (e) {
    throw toLinkError(e)
  }
}
